import WaveDivider from "../../home/components/WaveDivider";
import type { ServiceTier, ServicePageData } from "../../../mocks/servicePages";

interface PricingSectionProps {
  tiers: ServiceTier[];
  accepts: string[];
  service: ServicePageData;
  onBookNow: () => void;
}

export default function PricingSection({ tiers, accepts, service, onBookNow }: PricingSectionProps) {
  return (
    <div>
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-5 thick-border cartoon-shadow-sm">
              <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Simple Pricing</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-cl-dark leading-tight mb-4">
              Honest prices,<br />no hidden extras.
            </h2>
            <p className="text-cl-dark/60 text-base font-semibold max-w-lg mx-auto">
              Fixed-price quotes before we arrive. What you see is what you pay.
            </p>
          </div>

          <div className={`grid gap-6 mb-16 ${
            tiers.length === 1
              ? "max-w-md mx-auto"
              : tiers.length === 2
              ? "grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto"
              : tiers.length >= 5
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto"
          }`}>
            {tiers.map((tier) => (
              <div key={tier.label} className={`rounded-3xl p-6 flex flex-col cartoon-card ${tier.popular ? "bg-cl-dark" : "bg-white"}`}>
                {tier.popular && (
                  <div className="inline-flex items-center gap-1.5 bg-cl-green text-white text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 w-fit border-2 border-[#4a8c0a] cartoon-shadow-sm">
                    <i className="ri-star-fill text-xs"></i>
                    Most Popular
                  </div>
                )}

                <h3 className={`font-black text-lg mb-2 leading-tight ${tier.popular ? "text-white" : "text-cl-dark"}`}>{tier.label}</h3>

                <div className="flex items-end gap-1 mb-1">
                  <span className={`font-black text-3xl leading-none ${tier.popular ? "text-cl-lime" : "text-cl-dark"}`}>{tier.price}</span>
                </div>
                <p className={`text-xs mb-5 font-semibold ${tier.popular ? "text-white/60" : "text-gray-400"}`}>{tier.unit}</p>

                <ul className="flex flex-col gap-2.5 mb-6 flex-1">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <span className={`w-5 h-5 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 ${tier.popular ? "bg-cl-green border-[#4a8c0a]" : "bg-cl-lime border-cl-green"}`}>
                        <i className={`ri-check-line text-[10px] font-black ${tier.popular ? "text-white" : "text-cl-dark"}`}></i>
                      </span>
                      <span className={`text-xs font-semibold leading-snug ${tier.popular ? "text-white/80" : "text-cl-dark/80"}`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button onClick={onBookNow}
                  className={`w-full font-black py-3 rounded-2xl text-sm whitespace-nowrap ${tier.popular ? "bg-cl-green text-white cartoon-btn-green" : "bg-cl-dark text-white cartoon-btn"}`}>
                  Book Now →
                </button>
              </div>
            ))}
          </div>

          <div className="bg-cl-tint rounded-3xl p-8 thick-border cartoon-shadow">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8">
              <div className="lg:w-64 flex-shrink-0">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-cl-lime border-2 border-cl-dark mb-4 cartoon-shadow-sm">
                  <i className="ri-checkbox-circle-line text-cl-dark text-2xl"></i>
                </div>
                <h3 className="text-cl-dark font-black text-xl mb-2">What we accept</h3>
                <p className="text-cl-dark/60 text-sm font-semibold leading-relaxed">
                  {service.title} covers all of the following. Not sure? Call 03335 775445.
                </p>
              </div>
              <div className="flex-1 flex flex-wrap gap-3">
                {accepts.map((item) => (
                  <div key={item} className="flex items-center gap-2 bg-white rounded-full px-4 py-2 thick-border cartoon-shadow-sm">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-cl-green flex-shrink-0">
                      <i className="ri-check-line text-white text-xs"></i>
                    </span>
                    <span className="text-cl-dark text-sm font-bold whitespace-nowrap">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaveDivider fromColor="#ffffff" toColor="#f3f4f8" flip />
    </div>
  );
}
